import {
  Badge,
  Button,
  Divider,
  Group,
  NumberInput,
  Radio,
  Select,
  Stack,
  Stepper,
  Switch,
  Text,
  TextInput
} from '@mantine/core'
import { FormErrors, isNotEmpty, useForm } from '@mantine/form'
import { IconArrowLeft, IconArrowRight, IconCheck } from '@tabler/icons-react'
import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { CrossCameraPipelineWrite } from '@/api/crossCameraApi'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import {
  CrossCameraApp,
  CrossCameraPipeline,
  CrossCameraTrackingModel
} from '@/types/crossCamera'

enum Step {
  Name = 0,
  Application = 1,
  Matching = 2
}

const STEP_LABELS: Record<Step, string> = {
  [Step.Name]: 'crossCamera.pipelines.create.step.name',
  [Step.Application]: 'crossCamera.pipelines.create.step.application',
  [Step.Matching]: 'crossCamera.pipelines.create.step.matching'
}

const STEPS = [Step.Name, Step.Application, Step.Matching]
const LAST_STEP = STEPS[STEPS.length - 1]
const TRACK_PICTURES = { min: 3, max: 8 }
const SECONDS_PER_MINUTE = 60
const SECONDS_PER_HOUR = 3600

const DEFAULT_SETTINGS = {
  similarity_threshold: 0.6,
  candidate_retention_s: 300,
  rejoin_enabled: false,
  faces_enabled: false,
  search_min_similarity: 0.4,
  min_track_pictures: 6,
  rejoin_window_s: 3600,
  rejoin_threshold: 0.75
}

type CrossCameraPipelineFormValues = {
  name: string
  application_id: string
  model_id: string
  similarity_threshold: number
  candidate_retention_s: number
  rejoin_enabled: boolean
  faces_enabled: boolean
  search_min_similarity: number
  min_track_pictures: number
  rejoin_window_s: number
  rejoin_threshold: number
}

const STEP_FIELDS: Record<Step, (keyof CrossCameraPipelineFormValues)[]> = {
  [Step.Name]: ['name'],
  [Step.Application]: ['application_id'],
  [Step.Matching]: [
    'model_id',
    'similarity_threshold',
    'candidate_retention_s',
    'min_track_pictures',
    'search_min_similarity',
    'faces_enabled',
    'rejoin_enabled',
    'rejoin_window_s',
    'rejoin_threshold'
  ]
}

type CrossCameraPipelineFormProps = {
  pipeline?: CrossCameraPipeline
  applications: CrossCameraApp[]
  usedApplicationIds: string[]
  models: CrossCameraTrackingModel[]
  isSubmitting?: boolean
  onSubmit: (data: CrossCameraPipelineWrite) => void
  onCancel: () => void
}

export const CrossCameraPipelineForm = ({
  pipeline,
  applications,
  usedApplicationIds,
  models,
  isSubmitting,
  onSubmit,
  onCancel
}: CrossCameraPipelineFormProps) => {
  const intl = useIntl()
  const [step, setStep] = useState<Step>(Step.Name)

  const form = useForm<CrossCameraPipelineFormValues>({
    initialValues: pipeline
      ? {
          name: pipeline.name,
          application_id: pipeline.application.id,
          model_id: pipeline.model?.id ?? '',
          similarity_threshold: pipeline.similarity_threshold,
          candidate_retention_s: pipeline.candidate_retention_s,
          rejoin_enabled: pipeline.rejoin_enabled,
          faces_enabled: pipeline.faces_enabled,
          search_min_similarity: pipeline.search_min_similarity,
          min_track_pictures: pipeline.min_track_pictures,
          rejoin_window_s: pipeline.rejoin_window_s,
          rejoin_threshold: pipeline.rejoin_threshold
        }
      : { name: '', application_id: '', model_id: '', ...DEFAULT_SETTINGS },
    validate: {
      name: isNotEmpty(
        intl.formatMessage({ id: 'crossCamera.pipelines.create.nameRequired' })
      ),
      application_id: isNotEmpty(
        intl.formatMessage({
          id: 'crossCamera.pipelines.create.applicationRequired'
        })
      ),
      candidate_retention_s: (value) =>
        value > 0
          ? null
          : intl.formatMessage({
              id: 'crossCamera.settings.retention.required'
            }),
      rejoin_window_s: (value, values) => {
        if (!(value > 0)) {
          return intl.formatMessage({
            id: 'crossCamera.settings.rejoinWindow.required'
          })
        }
        return values.rejoin_enabled && value < values.candidate_retention_s
          ? intl.formatMessage({ id: 'crossCamera.settings.rejoin.tooShort' })
          : null
      }
    }
  })

  const isStepValid = (current: Step) =>
    STEP_FIELDS[current].every((field) => !form.validateField(field).hasError)

  const handleNextClick = () => {
    if (isStepValid(step)) {
      setStep((current) => Math.min(current + 1, LAST_STEP))
    }
  }

  const handleBackClick = () => {
    setStep((current) => Math.max(current - 1, Step.Name))
  }

  const handleValidationFailure = (errors: FormErrors) => {
    const invalidStep = STEPS.find((current) =>
      STEP_FIELDS[current].some((field) => field in errors)
    )

    if (invalidStep !== undefined) {
      setStep(invalidStep)
    }
  }

  const defaultModelId = models.find((model) => model.is_default)?.id ?? ''
  const selectedModelId = form.values.model_id || defaultModelId
  const isModelChanged =
    !!pipeline && selectedModelId !== (pipeline.model?.id ?? defaultModelId)

  const handleSubmit = ({
    model_id,
    ...values
  }: CrossCameraPipelineFormValues) => {
    if (!pipeline) {
      // Empty means the catalogue default, which the platform picks.
      onSubmit({ ...values, model_id: model_id || null })
      return
    }
    // The API fills in the default model, so sending it unchanged would pin the pipeline to it.
    onSubmit(isModelChanged ? { ...values, model_id: selectedModelId } : values)
  }

  const applicationOptions = pipeline
    ? [{ value: pipeline.application.id, label: pipeline.application.name }]
    : applications
        .filter((app) => !usedApplicationIds.includes(app.app_id))
        .map((app) => ({ value: app.app_id, label: app.app_name }))

  return (
    <form
      noValidate
      onSubmit={form.onSubmit(handleSubmit, handleValidationFailure)}
    >
      <Stepper active={step} size="sm" mb="lg" onStepClick={setStep}>
        {STEPS.map((value) => (
          <Stepper.Step
            key={value}
            label={intl.formatMessage({ id: STEP_LABELS[value] })}
          />
        ))}
      </Stepper>

      <Stack gap="md" mih={260}>
        {step === Step.Name && (
          <TextInput
            label={<FormattedMessage id="crossCamera.pipelines.fields.name" />}
            description={
              <FormattedMessage id="crossCamera.pipelines.create.nameDescription" />
            }
            {...form.getInputProps('name')}
          />
        )}

        {step === Step.Application && (
          <>
            <Select
              label={
                <FormattedMessage id="crossCamera.pipelines.fields.application" />
              }
              description={
                <FormattedMessage id="crossCamera.pipelines.create.applicationDescription" />
              }
              data={applicationOptions}
              disabled={!!pipeline}
              allowDeselect={false}
              searchable
              {...form.getInputProps('application_id')}
            />

            <PlatformAlert variant="info" compact>
              <FormattedMessage id="crossCamera.pipelines.create.groupsNote" />
            </PlatformAlert>
          </>
        )}

        {step === Step.Matching && (
          <>
            <Radio.Group
              label={<FormattedMessage id="crossCamera.tracking.model.title" />}
              description={
                <FormattedMessage id="crossCamera.tracking.model.description" />
              }
              value={selectedModelId}
              onChange={(value) => form.setFieldValue('model_id', value)}
            >
              <Stack gap="xs" mt="xs">
                {models.map((model) => (
                  <Radio.Card
                    key={model.id}
                    value={model.id}
                    p="sm"
                    radius="md"
                  >
                    <Group wrap="nowrap" align="flex-start">
                      <Radio.Indicator />

                      <Stack gap={2}>
                        <Group gap="xs">
                          <Text fw={600} size="sm">
                            <span>{model.name}</span>
                          </Text>

                          {model.is_default && (
                            <Badge size="xs" color="gray" tt="none">
                              <FormattedMessage id="crossCamera.tracking.model.default" />
                            </Badge>
                          )}
                        </Group>

                        <Text size="xs" c="dimmed">
                          <span>{model.description}</span>
                        </Text>
                      </Stack>
                    </Group>
                  </Radio.Card>
                ))}
              </Stack>
            </Radio.Group>

            {isModelChanged && (
              <PlatformAlert variant="warning" compact>
                <FormattedMessage id="crossCamera.tracking.model.warning" />
              </PlatformAlert>
            )}

            <NumberInput
              label={
                <FormattedMessage id="crossCamera.settings.threshold.label" />
              }
              description={
                <FormattedMessage id="crossCamera.settings.threshold.description" />
              }
              min={0}
              max={1}
              step={0.05}
              decimalScale={2}
              {...form.getInputProps('similarity_threshold')}
            />

            <PlatformAlert variant="info" compact>
              <FormattedMessage id="crossCamera.settings.guidance" />
            </PlatformAlert>

            <NumberInput
              label={
                <FormattedMessage id="crossCamera.settings.retention.label" />
              }
              description={
                <FormattedMessage id="crossCamera.settings.retention.description" />
              }
              min={1}
              max={1440}
              suffix=" min"
              decimalScale={1}
              value={form.values.candidate_retention_s / SECONDS_PER_MINUTE}
              error={form.errors.candidate_retention_s}
              onChange={(value) =>
                form.setFieldValue(
                  'candidate_retention_s',
                  Math.round(Number(value) * SECONDS_PER_MINUTE)
                )
              }
            />

            <NumberInput
              label={
                <FormattedMessage id="crossCamera.settings.minTrackPictures.label" />
              }
              description={
                <FormattedMessage
                  id="crossCamera.settings.minTrackPictures.description"
                  values={TRACK_PICTURES}
                />
              }
              min={TRACK_PICTURES.min}
              max={TRACK_PICTURES.max}
              allowDecimal={false}
              {...form.getInputProps('min_track_pictures')}
            />

            <NumberInput
              label={
                <FormattedMessage id="crossCamera.settings.searchMinimum.label" />
              }
              description={
                <FormattedMessage id="crossCamera.settings.searchMinimum.description" />
              }
              min={0}
              max={1}
              step={0.05}
              decimalScale={2}
              {...form.getInputProps('search_min_similarity')}
            />

            <Switch
              label={<FormattedMessage id="crossCamera.settings.faces.label" />}
              description={
                <FormattedMessage id="crossCamera.settings.faces.description" />
              }
              {...form.getInputProps('faces_enabled', { type: 'checkbox' })}
            />

            <Divider
              label={<FormattedMessage id="crossCamera.settings.returning" />}
              labelPosition="left"
            />

            <Switch
              label={
                <FormattedMessage id="crossCamera.settings.rejoin.label" />
              }
              description={
                <FormattedMessage id="crossCamera.settings.rejoin.description" />
              }
              {...form.getInputProps('rejoin_enabled', { type: 'checkbox' })}
            />

            <NumberInput
              label={
                <FormattedMessage id="crossCamera.settings.rejoinWindow.label" />
              }
              description={
                <FormattedMessage id="crossCamera.settings.rejoinWindow.description" />
              }
              min={1}
              max={24}
              suffix=" h"
              decimalScale={1}
              disabled={!form.values.rejoin_enabled}
              value={form.values.rejoin_window_s / SECONDS_PER_HOUR}
              error={form.errors.rejoin_window_s}
              onChange={(value) =>
                form.setFieldValue(
                  'rejoin_window_s',
                  Math.round(Number(value) * SECONDS_PER_HOUR)
                )
              }
            />

            <NumberInput
              label={
                <FormattedMessage id="crossCamera.settings.rejoinThreshold.label" />
              }
              description={
                <FormattedMessage id="crossCamera.settings.rejoinThreshold.description" />
              }
              min={0}
              max={1}
              step={0.05}
              decimalScale={2}
              disabled={!form.values.rejoin_enabled}
              {...form.getInputProps('rejoin_threshold')}
            />
          </>
        )}
      </Stack>

      <Group justify="space-between" mt="xl">
        <Button
          variant="subtle"
          leftSection={<IconArrowLeft size={16} />}
          disabled={step === Step.Name}
          onClick={handleBackClick}
        >
          <FormattedMessage id="back" />
        </Button>

        <Group>
          <Button variant="subtle" onClick={onCancel}>
            <FormattedMessage id="cancel" />
          </Button>

          {/* Keyed apart: reusing the clicked Next button as Save made that click submit. */}
          {step === LAST_STEP ? (
            <Button
              key="save"
              type="submit"
              leftSection={<IconCheck size={16} />}
              loading={isSubmitting}
            >
              <FormattedMessage id="save" />
            </Button>
          ) : (
            <Button
              key="next"
              rightSection={<IconArrowRight size={16} />}
              onClick={handleNextClick}
            >
              <FormattedMessage id="next" />
            </Button>
          )}
        </Group>
      </Group>
    </form>
  )
}
